import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAppSelector } from "../../../app/hooks";
import { getParamAsInt } from "../../../app/utils";
import RtkqResultViewer from "../../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../../components/input/SelectDevelopmentView";
import {
	SelectOrganizerDto,
	SelectOrganizerEditModel,
	NullSelectOrganizerEditModel,
	useSelectOrganizerByDraftQuery,
	useSelectOrganizerByPromptQuery,
	useLazyGraphicOrganizerPrintableLinkQuery,
	useSelectOrganizerMutation,
	SelectOrganizerViewModel,
	TAP_GUIDE_ORGANIZER_NAME,
} from "./organizingApiSlice";

export default function SelectOrganizer() {
	const [queryParams] = useSearchParams();
	const draftId = getParamAsInt(queryParams, "draftId");
	const promptId = getParamAsInt(queryParams, "promptId");

	const [view, setView] = useState<string>("legacy");
	const [organizerData, setOrganizerData] = useState<SelectOrganizerViewModel>();
	const [currentOrganizer, setOrganizer] = useState<SelectOrganizerDto | undefined>();

	const navigate = useNavigate();

	useEffect(() => {
		if (draftId === undefined && promptId === undefined) {
			navigate(`/writing`);
		}
	}, []);

	const buildEditModel = (
		organizer: SelectOrganizerDto | undefined,
		draftId?: number,
		promptId?: number
	): SelectOrganizerEditModel => {
		if (organizer && draftId) {
			return { organizerId: organizer.id, draftId: draftId, promptId: null };
		} else if (organizer && promptId) {
			return { organizerId: organizer.id, draftId: null, promptId: promptId };
		}
		return NullSelectOrganizerEditModel;
	};
	const editModel = buildEditModel(currentOrganizer, draftId, promptId);

	const result = draftId
		? useSelectOrganizerByDraftQuery(draftId ?? skipToken)
		: useSelectOrganizerByPromptQuery(promptId ?? skipToken);

	const [getPrintable, getPrintableResult] = useLazyGraphicOrganizerPrintableLinkQuery();
	const [selectOrganizer, selectOrganizerResult] = useSelectOrganizerMutation();

	useEffect(() => {
		if (!result?.data) return;
		const _data = { ...result.data };
		_data.recommendedOrganizers = _data.recommendedOrganizers?.filter(
			(o) => o.name !== TAP_GUIDE_ORGANIZER_NAME
		);
		_data.otherOrganizers = _data.otherOrganizers?.filter(
			(o) => o.name !== TAP_GUIDE_ORGANIZER_NAME
		);
		setOrganizerData(_data);
	}, [result]);

	const handlePreviousClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
		e.preventDefault();
		navigate(`/writing`);
	};

	const handleNextClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
		e.preventDefault();

		if (currentOrganizer) {
			selectOrganizer(editModel)
				.unwrap()
				.then((payload) => {
					const draftId = payload.draftId;
					navigate(`/writing/organize?draftId=${draftId}`);
				});
		}
	};

	const handlePrintClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
		e.preventDefault();

		if (currentOrganizer) {
			getPrintable(currentOrganizer.id)
				.unwrap()
				.then((data) => {
					window.open(data.url);
				});
		}
	};

	const handleOrganizerClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
		e.preventDefault();

		const current = result?.data?.recommendedOrganizers
			.concat(result?.data?.otherOrganizers)
			.find((o) => o.id.toString() === e.currentTarget.dataset.id);
		setOrganizer(current);
		editModel.organizerId = current?.id ?? null;

		const organizers = document.querySelectorAll(".organizer");
		organizers.forEach((organizer) => {
			organizer.classList.remove("active");
		});

		e.currentTarget.parentElement?.classList.add("active");
	};

	const handleOrganizerDoubleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
		e.preventDefault();

		// handleOrganizerClick() always gets called/completes first when double clicking on organizer,
		// so the logic for finding the current organizer is handled in handleOrganizerClick()

		if (editModel.organizerId) {
			selectOrganizer(editModel)
				.unwrap()
				.then((payload) => {
					const draftId = payload.draftId;
					navigate(`/writing/organize?draftId=${draftId}`);
				});
		}
	};

	if (draftId === undefined && promptId === undefined) return null;

	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Select an Organizer</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "legacy", label: "Legacy" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64"
				/>
			</div>

			{result && (
				<>
					{(result.isUninitialized || result.isFetching) && (
						<section className="mt-8">Loading organizers...</section>
					)}
					{result.isError && (
						<section className="mt-8">There was an error loading organizers...</section>
					)}
					{organizerData && result.status === "fulfilled" && view == "legacy" && (
						<section className="mt-8">
							<p className="mt-4">
								Select an organizer and click <b>Start Organizing</b> to begin the
								prewriting exercise or click <b>Change Prompt</b> to go back to the
								Writing Hub. To download a printable version of the organizer,
								select an organizer and click <b>Print</b>.
							</p>
							{organizerData.requiredOrganizer && (
								<section className="mt-8">
									<h3>Assigned Organizer</h3>
									{organizerData.requiredOrganizer.name ==
									TAP_GUIDE_ORGANIZER_NAME ? (
										<p>
											Sorry, but this site does currently support the{" "}
											{TAP_GUIDE_ORGANIZER_NAME} organizer.
										</p>
									) : (
										<ul className="flex">
											<OrganizerEntry
												key={organizerData.requiredOrganizer.id}
												organizer={organizerData.requiredOrganizer}
												onClick={handleOrganizerClick}
												onDoubleClick={handleOrganizerDoubleClick}
											/>
										</ul>
									)}
								</section>
							)}
							{!organizerData.requiredOrganizer &&
								organizerData.recommendedOrganizers &&
								organizerData.recommendedOrganizers.length > 0 && (
									<section className="mt-8">
										<h3>Recommended</h3>
										<ul className="flex flex-wrap ">
											{organizerData.recommendedOrganizers.map(
												(item: SelectOrganizerDto) => (
													<OrganizerEntry
														key={item.id}
														organizer={item}
														onClick={handleOrganizerClick}
														onDoubleClick={handleOrganizerDoubleClick}
													/>
												)
											)}
										</ul>
									</section>
								)}
							{!organizerData.requiredOrganizer &&
								organizerData.otherOrganizers &&
								organizerData.otherOrganizers.length > 0 && (
									<section className="mt-8">
										<h3 className="mt-8">Other Organizers</h3>
										<ul className="flex flex-wrap">
											{organizerData.otherOrganizers.map(
												(item: SelectOrganizerDto) => (
													<OrganizerEntry
														key={item.id}
														organizer={item}
														onClick={handleOrganizerClick}
														onDoubleClick={handleOrganizerDoubleClick}
													/>
												)
											)}
										</ul>
									</section>
								)}
							<div className="flex justify-between mt-8">
								<div>
									<button
										id="change-prompt-button"
										className="btn btn-neutral mr-2"
										onClick={handlePreviousClick}>
										Change Prompt
									</button>
								</div>
								<div>
									<button
										id="print-organizer-button"
										className="btn btn-neutral mr-2"
										onClick={handlePrintClick}
										disabled={!currentOrganizer}>
										Print
									</button>
									<button
										id="start-organizing-button"
										className="btn btn-alert mr-2"
										onClick={handleNextClick}
										disabled={!currentOrganizer}>
										Start Organizing
									</button>
								</div>
							</div>
						</section>
					)}
				</>
			)}

			{view == "raw" && <RtkqResultViewer heading="Select Organizer" result={result} />}
		</section>
	);
}

interface OrganizerEntryProps {
	className?: string;
	organizer: SelectOrganizerDto;
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onDoubleClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}
function OrganizerEntry({ className, organizer, onClick, onDoubleClick }: OrganizerEntryProps) {
	const { aspUrl } = useAppSelector((state) => state.site);

	// remove IIS virtual directory names like "/miwrite/"
	const thumbUrl = organizer.thumbURL.toLowerCase().startsWith("/images")
		? `${aspUrl}${organizer.thumbURL}`
		: `${aspUrl}/${organizer.thumbURL.replace(/^\/[^\/]*\//, "")}`;

	const id = "organizer-" + organizer.id.toString();

	return (
		<li
			key={organizer.id}
			className="organizer flex-none text-sm my-2 p-2 text-center w-36 hover:bg-primary-50 [&.active]:bg-primary-100 [&.active]:hover:bg-primary-200">
			<button
				id={id}
				className="cursor-pointer text-black font-button"
				data-id={organizer.id}
				onClick={onClick}
				onDoubleClick={onDoubleClick}>
				<img
					src={thumbUrl}
					alt={organizer.formattedName}
					className="h-20 w-20 mx-auto mb-2 organizer-image"
				/>
				<label className="block">{organizer.formattedName}</label>
			</button>
		</li>
	);
}
